import type { BrandAssetBackground, BrandAssetType, KnowledgeBaseFileViewDto } from '@/types/knowledgeBase'

/**
 * Brand Guidelines' logo files (ORB-968). A mark can have a version for light backgrounds and one for dark ones, and
 * emails and landing pages use the one made for the background they place it on. A file marked for neither is used on
 * both, which is every file uploaded before ORB-968. A slot is a type and a background, so each version has its own.
 */

export type MarkType = Exclude<BrandAssetType, 'OgImage'>

export interface SlotInfo<Type extends BrandAssetType = BrandAssetType> {
  type: Type
  label: string
  /** Where Webster places it. */
  usage: string
}

/** The marks that come in a light and a dark version, in the page's order. */
export const MARK_SLOTS: readonly SlotInfo<MarkType>[] = [
  { type: 'Logo', label: 'Logo', usage: 'The wordmark, in email headers and page navigation.' },
  { type: 'Banner', label: 'Banner', usage: 'A wide lockup across the top of an email.' },
  { type: 'Icon', label: 'Icon', usage: 'The mark on its own, where the wordmark will not fit.' },
  { type: 'Favicon', label: 'Favicon', usage: 'The browser tab icon on landing pages.' },
]

/** The social image is never marked: it carries its own background. */
export const SOCIAL_SLOT: SlotInfo<'OgImage'> = { type: 'OgImage', label: 'Social image', usage: 'Shown when a landing page is shared.' }

export const BACKGROUNDS: readonly BrandAssetBackground[] = ['Light', 'Dark']

/** What a column shows: the version made for its background, or the unmarked file it is used in place of. */
export interface MarkCell {
  file: KnowledgeBaseFileViewDto
  marked: boolean
}

export type MarkRow =
  /** Only a file marked for neither background: shown once, across both, since it is used on both. */
  | { kind: 'either'; file: KnowledgeBaseFileViewDto }
  /**
   * A column for each background. An unmarked file shows in the column with no version of its own, where it is used;
   * one that both backgrounds have their own version over is used nowhere, and is listed so it can be removed.
   */
  | { kind: 'split'; cells: Record<BrandAssetBackground, MarkCell | null>; unused: KnowledgeBaseFileViewDto[] }

export function markRow(files: readonly KnowledgeBaseFileViewDto[], type: MarkType): MarkRow {
  const ofType = files.filter(file => file.AssetType === type)
  const marked = (background: BrandAssetBackground) => ofType.find(file => file.Background === background) ?? null
  const light = marked('Light')
  const dark = marked('Dark')
  const [unmarked = null, ...more] = ofType.filter(file => !file.Background)
  if (light === null && dark === null && unmarked !== null) return { kind: 'either', file: unmarked }

  const cell = (version: KnowledgeBaseFileViewDto | null): MarkCell | null =>
    version !== null ? { file: version, marked: true } : unmarked !== null ? { file: unmarked, marked: false } : null
  return {
    kind: 'split',
    cells: { Light: cell(light), Dark: cell(dark) },
    unused: light !== null && dark !== null && unmarked !== null ? [unmarked, ...more] : more,
  }
}

/** A slot's name in a sentence: "the logo for light backgrounds", "a favicon for dark backgrounds". */
export function slotPhrase(slot: SlotInfo, article: 'the' | 'a', background?: BrandAssetBackground | null): string {
  const label = slot.label.toLowerCase()
  const determiner = article === 'a' && /^[aeiou]/.test(label) ? 'an' : article
  return `${determiner} ${label}${background ? ` for ${background.toLowerCase()} backgrounds` : ''}`
}
